import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

/**
 * Hook to monitor network connectivity status
 * Shows toast notifications when connection is lost or restored
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      
      // Only show reconnection message if we were offline before
      if (wasOffline) {
        toast({
          title: "Connection Restored",
          description: "You're back online!",
        });
        setWasOffline(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      
      toast({
        title: "Connection Lost",
        description: "You're currently offline. Some features may not work.",
        variant: "destructive",
      });
    };

    // Add event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [toast, wasOffline]);

  return { isOnline };
};
